import Hero from "@/components/Hero";
import FinalCTA from "@/components/FinalCTA";
import {
  Cpu, Layers, Factory, Wrench, Hammer, PackageCheck, Truck, ShieldCheck,
  Sparkles, Award, Users2, Cog, BadgeCheck, Tag, Headphones, Check,
} from "lucide-react";

const services = [
  {
    icon: Cpu,
    title: "Progettazione Prodotti Personalizzati e Soluzioni OEM/ODM",
    body: "Offriamo servizi di progettazione meccanica ed elettronica, modellazione 3D, sviluppo prodotto, prototipazione rapida con CNC e stampa 3D. Sviluppiamo soluzioni OEM/ODM su misura, dall'idea alla produzione.",
    bullets: ["Progettazione meccanica & elettronica", "Modellazione 3D e simulazioni", "Prototipazione CNC e stampa 3D", "Soluzioni OEM/ODM"],
  },
  {
    icon: Layers,
    title: "Progettazione e Produzione Stampi",
    body: "Progettiamo e produciamo stampi di precisione con lavorazioni CNC, elettroerosione e fresatura ad alta velocità. Eseguiamo stampaggio di prova, ottimizzazione e manutenzione completa.",
    bullets: ["Progettazione e costruzione stampi", "Elettroerosione (EDM) e fresatura HSC", "Stampaggio di prova e validazione", "Manutenzione e ottimizzazione"],
  },
  {
    icon: Factory,
    title: "Produzione di Prodotti in Plastica",
    body: "Stampaggio a iniezione su larga scala, finitura superficiale, verniciatura, rivestimento, serigrafia e incisione laser per componenti plastici tecnici ed estetici.",
    bullets: ["Stampaggio a iniezione tecnico", "Verniciatura e rivestimenti", "Serigrafia e tampografia", "Incisione e marcatura laser"],
  },
  {
    icon: Wrench,
    title: "Fabbricazione di Prodotti Metallici",
    body: "Stampaggio metalli, lavorazioni CNC, fabbricazione lamiere e produzione di componenti metallici con trattamenti superficiali di qualità.",
    bullets: ["Stampaggio metalli e tranciatura", "Lavorazioni CNC su acciaio, alluminio, ottone", "Fabbricazione lamiere", "Trattamenti superficiali"],
  },
  {
    icon: Cog,
    title: "Produzione Componenti CNC",
    body: "Lavorazioni di precisione per componenti personalizzati su basse e alte tirature, in materiali come alluminio, acciaio inox, ottone e plastica tecnica.",
    bullets: ["Tornitura e fresatura CNC", "Tolleranze strette di precisione", "Basse e alte tirature", "Alluminio, inox, ottone, plastica"],
  },
  {
    icon: Hammer,
    title: "Assemblaggio e Imballaggio",
    body: "Assemblaggio completo del prodotto, test funzionali in produzione e confezionamento personalizzato pronto per la distribuzione.",
    bullets: ["Assemblaggio manuale e semi-automatico", "Test funzionali e qualità", "Packaging personalizzato", "Etichettatura e tracciabilità"],
  },
  {
    icon: Truck,
    title: "Logistica e Consegna",
    body: "Spedizioni globali, gestione supply chain, gestione scorte e consegne puntuali in oltre 30 paesi.",
    bullets: ["Spedizioni internazionali", "Gestione supply chain", "Magazzino e scorte", "Consegne puntuali"],
  },
  {
    icon: ShieldCheck,
    title: "Controllo Qualità",
    body: "Certificazione ISO 9001, controlli dimensionali in entrata e in produzione, qualità stabile e affidabile garantita su ogni lotto.",
    bullets: ["Certificazione ISO 9001", "Controlli dimensionali (CMM)", "Ispezioni in linea", "Tracciabilità lotti"],
  },
];

const benefits = [
  { icon: Sparkles, label: "Soluzione completa" },
  { icon: Award, label: "Oltre 30 anni di esperienza" },
  { icon: Users2, label: "Team tecnico specializzato" },
  { icon: Cpu, label: "Tecnologia avanzata" },
  { icon: ShieldCheck, label: "Controllo qualità rigoroso" },
  { icon: Tag, label: "Prezzi competitivi" },
  { icon: Truck, label: "Logistica affidabile" },
  { icon: Headphones, label: "Supporto clienti dedicato" },
];

export default function Servizi() {
  return (
    <>
      <Hero
        testId="servizi-hero"
        desktopImg="/images/hero/ServiziHERODesktop.jpeg"
        mobileImg="/images/hero/ServiziHEROMobile.png"
        eyebrow="I NOSTRI SERVIZI"
        title="Servizi di Progettazione, Produzione"
        titleAccent="e Assemblaggio Industriale"
        subtitle="Drosun offre servizi completi dalla progettazione del prodotto alla produzione di massa, garantendo qualità, efficienza e supporto tecnico in ogni fase del progetto."
        ctaLabel="Richiedi una consulenza tecnica"
      />

      {/* Intro */}
      <section className="py-20 bg-white" data-testid="servizi-intro">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="inline-block w-10 h-[3px] bg-drosun-red" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-drosun-muted">Approccio integrato</span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl text-drosun-text leading-tight">
            Soluzioni industriali complete<br className="hidden sm:block" /> per ogni fase del progetto
          </h2>
          <p className="mt-7 text-[18px] leading-relaxed text-drosun-muted">
            <strong>DROSUN</strong> è molto più di un semplice produttore: siamo un fornitore di soluzioni. Aiutiamo i clienti a risolvere problemi tecnici, migliorare i processi e trasformare idee in prodotti concreti attraverso un approccio integrato, innovativo e personalizzato.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-drosun-light pb-20" data-testid="servizi-list">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-7">
          {services.map((s, i) => (
            <article
              key={i}
              className="reveal group relative rounded-2xl bg-white border border-neutral-200 p-7 sm:p-9 shadow-[0_10px_40px_-26px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_60px_-26px_rgba(229,57,53,0.35)] transition-shadow"
              data-testid={`servizi-item-${i + 1}`}
            >
              <div className="absolute top-7 right-7 font-display text-3xl text-drosun-red/80">{String(i + 1).padStart(2, "0")}</div>
              <span className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-drosun-navy text-white">
                <s.icon size={26} strokeWidth={1.6} />
              </span>
              <h3 className="mt-5 font-display uppercase text-2xl sm:text-[28px] leading-tight text-drosun-text pr-12">{s.title}</h3>
              <p className="mt-4 text-[16px] leading-relaxed text-drosun-muted">{s.body}</p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2 text-[14.5px] text-drosun-text">
                    <Check size={18} className="text-drosun-red shrink-0 mt-0.5" /> <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Why Drosun */}
      <section className="py-20 bg-white" data-testid="servizi-why">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="inline-block w-10 h-[3px] bg-drosun-red" />
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-drosun-muted">Perché Drosun</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl text-drosun-text">Perché scegliere Drosun</h2>
            <p className="mt-5 text-[17px] text-drosun-muted">Un partner industriale completo, affidabile e orientato alla qualità.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {benefits.map((b, i) => (
              <div key={i} className="reveal rounded-xl border border-neutral-200 bg-drosun-light p-6 flex flex-col items-center text-center hover:border-drosun-red transition-colors">
                <span className="w-14 h-14 rounded-full bg-white border border-neutral-200 inline-flex items-center justify-center text-drosun-navy">
                  <b.icon size={26} strokeWidth={1.5} />
                </span>
                <span className="mt-4 font-semibold text-drosun-text text-[15px]">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA
        testId="servizi-final-cta"
        title="Inizia il tuo prossimo progetto industriale"
        subtitle="Richiedi una consulenza tecnica al nostro team."
        primary={{ label: "Richiedi una consulenza tecnica", to: "/contatti" }}
        secondary={{ label: "Contattaci", to: "/contatti" }}
      />
    </>
  );
}
